import { derivePiEligibleModels, isBrainPoolEntry, isClaudeFamilyModel, isPersonalWalletGroup, modelRoleAvailability, stripModelSuffixTag } from "./model-registry.js";
import { COST_TIERS } from "./model-cost-tiers.js";
const baseOf = (modelId) => stripModelSuffixTag(modelId).trim();
const uniqueSorted = (ids) => [...new Set([...ids].map(baseOf).filter(Boolean))].sort((a, b) => a.localeCompare(b));
/**
 * 网关清单里两个池的候选 base id = **超管此刻能勾的候选**。超管页记 `reviewedModels`、下发并入、组织层候选
 * （{@link orgBrainSelectionCandidates}）用的是同一份。
 *
 * N-115（产品经理 2026-10-08 拍板「上游此刻不提供的模型，超管后台不可以选」）：清单里有行、但该腿的全部协议
 * 都 `available: false`（账号维护 / 无凭据 / 配额耗尽…）的模型**不是候选**——超管页的复选框对它禁用
 * （`modelRoleAvailability(...).blocked`，同一判定），下发并入也就不替超管勾它；上游恢复后它才回到候选里。
 * 字段缺席（旧网关）= `unknown` = 不阻断，退回 N-75 原样。已在池里的模型不受影响：剔除只认「一行都没有」
 * （{@link isRetiredFromCatalog}），暂不可用的留在池里、由员工端置灰 / 接管。
 *
 * Claude 池候选 = 有原生 Anthropic 腿的模型（{@link isBrainPoolEntry}）。
 */
export function brainPoolCandidates(catalog) {
    const rowsByBase = new Map();
    for (const entry of catalog) {
        const base = baseOf(entry.modelId);
        const rows = rowsByBase.get(base) ?? [];
        rows.push(entry);
        rowsByBase.set(base, rows);
    }
    const selectable = (role) => (modelId) => !modelRoleAvailability(rowsByBase.get(baseOf(modelId)) ?? [], role).blocked;
    return {
        claude: uniqueSorted(catalog.filter(isBrainPoolEntry).map((entry) => entry.modelId)).filter(selectable("brain")),
        pi: uniqueSorted(derivePiEligibleModels(catalog)).filter(selectable("pi")),
    };
}
/**
 * 保存时该记成「见过的候选」的集合（N-115）：**此刻能勾的候选 ∪（上次记录 ∩ 仍在清单里）**。
 *
 * 只记当前候选会把两种事混成一种：上游维护期保存一次，超管在模型可选时明确不勾的（上次记录里有）会被
 * 忘掉——恢复后按「新候选」并入，推翻超管的决定；而维护期才出现、超管从没能决定的，不在任何记录里，
 * 恢复后按 N-75 自动并入——这正是「恢复后自动回来」。已下线（清单里一行都没有）的记录丢掉，不让它
 * 永远占着。`present` 用清单行即可（只看 `modelId`）。
 */
export function rememberReviewedModels(previous, selectable, present) {
    const presentBases = new Set(present.map((entry) => baseOf(entry.modelId)));
    const kept = (previous ?? []).filter((modelId) => presentBases.has(baseOf(modelId)));
    return uniqueSorted([...selectable, ...kept]);
}
/** 该 id（去掉 `[pi]` / `[1m]` 等标签后）在清单里一行都没有。空清单不可信，恒返回 false。 */
export function isRetiredFromCatalog(modelId, catalog) {
    if (catalog.length === 0)
        return false;
    const base = baseOf(modelId);
    return !catalog.some((entry) => baseOf(entry.modelId) === base);
}
/**
 * 一个白名单池的「先剔后并」。**空池不动**：Claude 池空 = 全放行（新模型本来就能用），
 * Pi 池空 = 不开放——往空池里并新候选会把「关着」翻成「开着」，同剔空保护一个道理，不做。
 */
function resolveAllowlist(list, reviewed, candidates, alive) {
    const original = list ?? [];
    const unchanged = { list: list ? [...list] : undefined, changed: false, removed: [], retained: [], added: [] };
    if (original.length === 0)
        return unchanged;
    const kept = original.filter(alive);
    const stale = original.filter((modelId) => !alive(modelId));
    const keptBases = new Set(kept.map(baseOf));
    const reviewedBases = reviewed ? new Set(reviewed.map(baseOf)) : undefined;
    const added = reviewedBases
        ? candidates.filter((candidate) => !reviewedBases.has(candidate) && !keptBases.has(candidate))
        : [];
    const next = [...kept, ...added];
    if (next.length === 0 && original.length > 0) {
        return { ...unchanged, retained: stale };
    }
    if (stale.length === 0 && added.length === 0)
        return unchanged;
    return { list: next, changed: true, removed: stale, retained: [], added };
}
function pruneParamKeys(params, alive) {
    if (!params)
        return null;
    const entries = Object.entries(params);
    const kept = entries.filter(([modelId]) => alive(modelId));
    if (kept.length === entries.length)
        return null;
    return kept.length > 0 ? Object.fromEntries(kept) : undefined;
}
/**
 * 先剔除（下线）后并入（新上）。**调用方必须先自证 catalog 是 live 的**（见文件头）。
 */
export function resolveBrainGovernanceAgainstCatalog(governance, catalog) {
    const removed = [];
    const retained = [];
    const added = [];
    if (catalog.length === 0)
        return { governance, removed, retained, added };
    const liveBases = new Set(catalog.map((entry) => baseOf(entry.modelId)));
    const alive = (modelId) => liveBases.has(baseOf(modelId));
    const candidates = brainPoolCandidates(catalog);
    const record = (sink, section, ids) => {
        for (const modelId of ids)
            sink.push({ section, modelId });
    };
    const next = { ...governance };
    let changed = false;
    const brain = governance.brainCuration;
    if (brain) {
        const outcome = resolveAllowlist(brain.allowedBrainModels, brain.reviewedModels, candidates.claude, alive);
        record(removed, "claude-brain", outcome.removed);
        record(retained, "claude-brain", outcome.retained);
        record(added, "claude-brain", outcome.added);
        const params = outcome.retained.length > 0 ? null : pruneParamKeys(brain.modelParams, alive);
        if (outcome.changed || params !== null) {
            const curation = { ...brain };
            if (outcome.changed)
                curation.allowedBrainModels = outcome.list;
            if (params !== null) {
                if (params)
                    curation.modelParams = params;
                else
                    delete curation.modelParams;
            }
            next.brainCuration = curation;
            changed = true;
        }
    }
    const pi = governance.piCuration;
    if (pi) {
        const outcome = resolveAllowlist(pi.models, pi.reviewedModels, candidates.pi, alive);
        record(removed, "pi-brain", outcome.removed);
        record(retained, "pi-brain", outcome.retained);
        record(added, "pi-brain", outcome.added);
        const params = outcome.retained.length > 0 ? null : pruneParamKeys(pi.modelParams, alive);
        if (outcome.changed || params !== null) {
            const curation = { ...pi };
            if (outcome.changed)
                curation.models = outcome.list;
            if (params !== null) {
                if (params)
                    curation.modelParams = params;
                else
                    delete curation.modelParams;
            }
            next.piCuration = curation;
            changed = true;
        }
    }
    const tierTable = governance.tierTable;
    if (tierTable) {
        const tiers = {};
        let tierChanged = false;
        // 运行时 guard：空档位表落 Mongo 时 `tiers: {}` 会被 minimize 掉，读回来没有这个字段
        // （2026-09-25 本机实跑：页面保存后直接崩在这里；服务端则被 catch 吞掉、整份收敛静默失效）。
        const storedTiers = tierTable.tiers;
        const rows = storedTiers && typeof storedTiers === "object" && !Array.isArray(storedTiers)
            ? Object.entries(storedTiers)
            : [];
        for (const [main, row] of rows) {
            if (!row || typeof row !== "object")
                continue;
            if (!alive(main)) {
                record(removed, "tier-table", [main]);
                tierChanged = true;
                continue;
            }
            const nextRow = {};
            for (const role of ["opus", "sonnet", "haiku"]) {
                const target = row[role];
                if (!target)
                    continue;
                if (alive(target))
                    nextRow[role] = target;
                else {
                    record(removed, "tier-table", [target]);
                    tierChanged = true;
                }
            }
            if (nextRow.opus || nextRow.sonnet || nextRow.haiku)
                tiers[main] = nextRow;
        }
        if (tierChanged) {
            next.tierTable = { ...tierTable, tiers };
            changed = true;
        }
    }
    const priority = governance.brainPriority;
    if (priority && priority.entries.length > 0) {
        const kept = priority.entries.filter((entry) => alive(entry.modelId));
        const stale = priority.entries.filter((entry) => !alive(entry.modelId)).map((entry) => entry.modelId);
        if (stale.length > 0 && kept.length === 0) {
            record(retained, "brain-priority", stale);
        }
        else if (stale.length > 0) {
            record(removed, "brain-priority", stale);
            next.brainPriority = { ...priority, entries: kept };
            changed = true;
        }
    }
    return { governance: changed ? next : governance, removed, retained, added };
}
// ──────────────────────────────────────────────────────────────────────
// 组织 / 角色主脑圈定（`ModelSelection.brainModels`）的「继承减排除」（N-87）
// ──────────────────────────────────────────────────────────────────────
/**
 * 组织层主脑圈定的候选 base id = 平台下发给本组织的 Claude 主脑池：{@link brainPoolCandidates} 的 Claude 池
 * （有原生 Anthropic 腿的模型），平台白名单非空时再按白名单收窄。与 `applyModelSelection` 里真正按
 * `brainModels` 判定的主脑线同一批模型。
 */
export function orgBrainSelectionCandidates(catalog, brainCuration) {
    const pool = brainPoolCandidates(catalog).claude;
    const allowed = brainCuration?.allowedBrainModels;
    if (!allowed || allowed.length === 0)
        return pool;
    const allowedBases = new Set(allowed.map(baseOf));
    return pool.filter((candidate) => allowedBases.has(candidate));
}
/** 下一层的候选 = 上一层候选 ∩ 上一层主脑圈定（上一层没圈定 = 原样）。角色层候选由此从组织层派生。 */
export function narrowBrainCandidatesBySelection(candidates, selection) {
    const brainModels = selection?.brainModels;
    if (!brainModels || brainModels.length === 0)
        return [...candidates];
    const selected = new Set(brainModels.map(baseOf));
    return candidates.filter((candidate) => selected.has(baseOf(candidate)));
}
/**
 * 组织 / 角色一层的主脑圈定按「继承减排除」对账（纯函数，N-87；维护者 2026-09-30 拍板 (d) + 存量 d2）。
 *
 * 判据与平台层（N-75）是**同一个** `resolveAllowlist`：先剔除失效 id（`alive` 判假的），再并入「当前候选里、
 * 不在见过的集合里、也不在列表里」的新候选；在见过的集合里却不在列表里的 = 管理员明确取消勾选过，不并入；
 * 剔完为空保留原样（空 `brainModels` = 继承全量，剔空会把「只允许这几个」翻成「全部放开」）。
 *
 * 与平台层唯一的差别是见过的集合缺省时怎么办：平台层「判不出谁是新的，不并入」；这里按 d2 把**列表本身**
 * 当见过的集合——存量显式列表上线即跟上平台，列表外的当前候选全部并入（已知副作用：原本想要封闭名单的
 * 组织会拿到列表外的模型；管理员下次保存时按真实候选集记下见过的集合，从此回到正常的排除语义）。
 *
 * - `candidates`：本层当前可选的主脑候选 base id（组织层 {@link orgBrainSelectionCandidates}；角色层再经
 *   {@link narrowBrainCandidatesBySelection} 按组织有效圈定收窄）。
 * - `alive`：该 id 在清单里是否还有行。**调用方必须先自证清单是 live 的**（同文件头）。
 */
export function resolveBrainSelectionAgainstCandidates(selection, candidates, alive) {
    const unchanged = { selection, removed: [], retained: [], added: [] };
    const brainModels = selection?.brainModels;
    if (!selection || !brainModels || brainModels.length === 0)
        return unchanged;
    const reviewed = selection.reviewedBrainModels ?? brainModels;
    const outcome = resolveAllowlist(brainModels, reviewed, candidates, alive);
    if (!outcome.changed)
        return { ...unchanged, retained: outcome.retained };
    return {
        selection: { ...selection, brainModels: outcome.list },
        removed: outcome.removed,
        retained: [],
        added: outcome.added,
    };
}
/** 审计/日志用的一行摘要；各层三类都为空 → undefined（= 与清单完全对齐）。 */
export function summarizeBrainSelectionDrift(drifts) {
    const parts = [];
    for (const drift of drifts) {
        for (const [label, ids] of [["removed", drift.removed], ["retained", drift.retained], ["added", drift.added]]) {
            if (ids.length > 0)
                parts.push(`${drift.scope} ${label}: ${ids.join(", ")}`);
        }
    }
    return parts.length > 0 ? parts.join("; ") : undefined;
}
/** 审计/日志用的一行摘要；三类都为空 → undefined（= 与清单完全对齐）。 */
export function summarizeBrainGovernanceDrift(resolution) {
    const fmt = (label, changes) => changes.length > 0 ? `${label}: ${changes.map((c) => `${c.section}:${c.modelId}`).join(", ")}` : undefined;
    const parts = [
        fmt("removed", resolution.removed),
        fmt("retained", resolution.retained),
        fmt("added", resolution.added),
    ].filter((part) => Boolean(part));
    return parts.length > 0 ? parts.join("; ") : undefined;
}
// ──────────────────────────────────────────────────────────────────────
// 管理员手填的模型 id（N-88）：云端任务模型清单、分组默认
// ──────────────────────────────────────────────────────────────────────
/**
 * 管理员手填的一个模型 id 是否已从网关清单下线（N-88）。判据就是 {@link isRetiredFromCatalog}（N-75 同一条：
 * 清单里一行都没有这个 base id），只多排除两类**本来就不在清单里**的值，否则会被当成下线误剔：
 *  · `inherit`（跟随主脑）；
 *  · Claude 裸别名（`sonnet` / `opus` / `haiku` / `fable` / `opusplan` / `default`，由 SDK 解析成具体 id）。
 * 具体的 `claude-*` id 照常判——企业网关同样供 Claude 主脑，它下线了就是下线了。
 * **调用方必须先自证 catalog 是 live 的**（同文件头）；空清单恒 false。
 */
export function isConfiguredModelRetired(modelId, catalog) {
    const base = baseOf(modelId);
    if (!base || base === "inherit")
        return false;
    if (!base.startsWith("claude-") && isClaudeFamilyModel(base))
        return false;
    return isRetiredFromCatalog(base, catalog);
}
/**
 * 组织模型清单（`org.modelBucketCuration`）剔掉已下线的行（N-88；N-167 起按成本档分键）。保序、保 `note`；
 * 一档剔空 = 该档跟随平台——与主脑白名单不同，这里**没有剔空保护**：留着全是死行的清单只会让任务逐个
 * 撞墙后整体失败，剔空才回到平台那份。`taskTypeTiers` 原样保留。剔完什么都不剩 → `undefined`
 *（整份未配置，与 bootstrap 省略空字段同一口径）。无变化返回入参原引用。
 * **调用方必须先自证 catalog 是 live 的**；空清单原样返回。维护中（`available:false`）的行不剔。
 */
export function pruneModelBucketCurationToCatalog(curation, catalog) {
    const removed = [];
    if (!curation || catalog.length === 0)
        return { curation, removed };
    const next = {};
    for (const bucket of COST_TIERS) {
        const candidates = curation[bucket]?.candidates;
        if (!candidates)
            continue;
        const kept = candidates.filter((candidate) => {
            if (!isConfiguredModelRetired(candidate.model, catalog))
                return true;
            removed.push({ bucket, model: candidate.model });
            return false;
        });
        if (kept.length === candidates.length)
            next[bucket] = curation[bucket];
        else if (kept.length > 0)
            next[bucket] = { candidates: kept };
    }
    if (removed.length === 0)
        return { curation, removed };
    if (curation.taskTypeTiers)
        next.taskTypeTiers = curation.taskTypeTiers;
    return { curation: Object.keys(next).length > 0 ? next : undefined, removed };
}
/**
 * 平台成本档清单剔掉已下线的条目（N-167，与 {@link pruneModelBucketCurationToCatalog} 同一判据与口径）：
 * 清单里一行都没有的 id 剔除，维护中不剔，剔空的档按未配置（键省略），不回写存储。无变化返回入参原引用。
 * **调用方必须先自证 catalog 是 live 的**；空清单原样返回。
 */
export function pruneModelCostTiersToCatalog(costTiers, catalog) {
    const removed = [];
    if (!costTiers || catalog.length === 0)
        return { costTiers, removed };
    const tiers = {};
    for (const tier of COST_TIERS) {
        const entries = costTiers.tiers[tier];
        if (!entries)
            continue;
        const kept = entries.filter((entry) => {
            if (!isConfiguredModelRetired(entry.modelId, catalog))
                return true;
            removed.push({ tier, modelId: entry.modelId });
            return false;
        });
        if (kept.length > 0)
            tiers[tier] = kept;
    }
    if (removed.length === 0)
        return { costTiers, removed };
    return { costTiers: { ...costTiers, tiers }, removed };
}
/**
 * 各组记住的默认模型里去掉已下线的（员工自配 `brainGroupParams` 与管理员下发 `brainGroupDefaults` 同形，
 * 共用这一份）。`defaultModel` 删掉后该组回落上一层默认；`roleMapping` 只删指向下线模型的那几档；删完一个键
 * 都不剩的组整条去掉。哪些组不归网关管（个人钱包组）由判官决定。无变化返回入参原引用。
 */
export function pruneRetiredGroupParams(params, judge) {
    if (!params)
        return params;
    let changed = false;
    const next = {};
    for (const [group, entry] of Object.entries(params)) {
        if (!entry || typeof entry !== "object") {
            next[group] = entry;
            continue;
        }
        const cleaned = { ...entry };
        let entryChanged = false;
        if (judge.isRetired(entry.defaultModel, group)) {
            delete cleaned.defaultModel;
            entryChanged = true;
        }
        if (entry.roleMapping && typeof entry.roleMapping === "object") {
            const mapping = Object.fromEntries(Object.entries(entry.roleMapping).filter(([, modelId]) => !judge.isRetired(modelId, group)));
            if (Object.keys(mapping).length !== Object.keys(entry.roleMapping).length) {
                if (Object.keys(mapping).length > 0)
                    cleaned.roleMapping = mapping;
                else
                    delete cleaned.roleMapping;
                entryChanged = true;
            }
        }
        if (!entryChanged) {
            next[group] = entry;
            continue;
        }
        changed = true;
        // 清空的组整条删掉：`brainGroupParams[组] ?? brainGroupDefaults[组]` 是整对象级 ??，
        // 留一个 `{}` 会把下一层的组默认挡住（model-registry resolveEffectiveBrainParams）。
        if (Object.keys(cleaned).length > 0)
            next[group] = cleaned;
    }
    return changed ? next : params;
}
/**
 * 管理员下发的分组默认（`managedSettings.brainGroupDefaults`）剔掉已下线的模型（N-88）：被剔的那一项视同
 * 未配置，员工回落跟随主脑 / 平台默认。个人钱包组（`claude-oauth` 等，`isPersonalWalletGroup`）不归网关
 * 清单管，整组不动。删空的组整条去掉；全空 → `{}`（调用方按「未配置」省略）。无变化返回入参原引用。
 * **调用方必须先自证 catalog 是 live 的**；空清单原样返回。
 */
export function pruneBrainGroupDefaultsToCatalog(defaults, catalog) {
    const removed = [];
    if (!defaults || catalog.length === 0)
        return { defaults, removed };
    const next = pruneRetiredGroupParams(defaults, {
        isRetired(modelId, group) {
            if (typeof modelId !== "string" || isPersonalWalletGroup(group))
                return false;
            if (!isConfiguredModelRetired(modelId, catalog))
                return false;
            removed.push({ group: group ?? "", modelId });
            return true;
        },
    });
    return { defaults: next, removed };
}
/** 服务端唯一构建器：live 清单 → 去重排序的 base id；空清单按 unavailable 处理。 */
export function buildLiveModelIdsResponse(catalog, fetchedAtMs) {
    const modelIds = catalog.source === "live" ? uniqueSorted(catalog.entries.map((entry) => entry.modelId)) : [];
    if (modelIds.length === 0 || fetchedAtMs === null)
        return { source: "unavailable", modelIds: [], fetchedAt: null };
    return { source: "live", modelIds, fetchedAt: new Date(fetchedAtMs).toISOString() };
}
/**
 * 客户端反序列化 guard：只有 `source === "live"`、`modelIds` 是非空字符串数组、`fetchedAt` 是字符串时
 * 才返回可用于判下线的行（`{ modelId }`）；其余一律 undefined（调用方据此「一个标记都不出」）。
 */
export function parseLiveModelIdsResponse(body) {
    if (typeof body !== "object" || body === null || Array.isArray(body))
        return undefined;
    const source = Reflect.get(body, "source");
    const modelIds = Reflect.get(body, "modelIds");
    const fetchedAt = Reflect.get(body, "fetchedAt");
    if (source !== "live" || typeof fetchedAt !== "string" || !Array.isArray(modelIds))
        return undefined;
    const rows = [];
    for (const id of modelIds) {
        if (typeof id !== "string" || id.trim().length === 0)
            return undefined;
        rows.push({ modelId: id });
    }
    return rows.length > 0 ? rows : undefined;
}
//# sourceMappingURL=brain-governance-reconcile.js.map